import { ReactNode, useCallback, useContext, useState } from "react"
import { Button, ButtonProps } from "@mezo-org/mezo-clay"
import { useSwitchChain } from "wagmi"

import { selectHasExpectedChain, useDappSelector } from "#/redux-state"
import { IS_MEZO_MAINNET, MEZO_CHAIN } from "#/shared/constants"
import { FlowContext } from "#/shared/context"
import { useAddMezoChainToWallet } from "#/shared/hooks"

type SwitchChainButtonProps = ButtonProps & {
  label: ReactNode
  chainId: number
}

export default function SwitchChainButton({
  label,
  chainId,
  children,
  ...props
}: SwitchChainButtonProps) {
  const [hasAttemptedToSwitch, setHasAttemptedToSwitch] = useState(false)

  const { isEthereumFlow } = useContext(FlowContext)

  const { isMezoChain, addMezoChain } = useAddMezoChainToWallet(isEthereumFlow)

  const hasCorrectChain = useDappSelector((state) =>
    selectHasExpectedChain(state, chainId),
  )

  const { switchChain } = useSwitchChain()

  const handleSwitchChain = useCallback(async () => {
    if (hasAttemptedToSwitch && chainId === MEZO_CHAIN.id && !isMezoChain) {
      await addMezoChain()
    }
    switchChain({ chainId })
    setHasAttemptedToSwitch(true)
  }, [addMezoChain, chainId, hasAttemptedToSwitch, isMezoChain, switchChain])

  if (hasCorrectChain) return children

  return (
    <Button
      type="submit"
      size="small"
      shape="pill"
      onClick={handleSwitchChain}
      {...props}
    >
      {hasAttemptedToSwitch && chainId === MEZO_CHAIN.id
        ? `Add ${IS_MEZO_MAINNET ? "Mezo" : "Mezo Testnet"} chain to wallet`
        : `Switch chain to ${label}`}
    </Button>
  )
}
